import { FeedFilterValues, useIntl, useMeFavouritePartner, useSettings } from '@capsicum/common';
import { type ChipListItem } from '@capsicum/common-web';
import {
	ChevronDownIcon,
	MarketplaceIcon,
	PollIcon,
	PuzzleIcon,
	ShirtAuctionIcon,
	SociosEmblemIcon
} from '@capsicum/common-web/icons';
import { FeedFiltersType } from '@capsicum/socios-core';
import { useMemo } from 'react';

type UsePostFiltersChipsProps = {
	filterChipsByPredicate: (chip: ChipListItem & { value: FeedFilterValues }) => boolean;
	type: FeedFiltersType;
};

export type PostFilterChipsShape = Array<ChipListItem & { value: FeedFilterValues }>;

export const usePostFiltersChips = ({ filterChipsByPredicate, type }: UsePostFiltersChipsProps) => {
	const { $t } = useIntl();
	const { settings } = useSettings();
	const { isFavouritePartnerSsu } = useMeFavouritePartner();

	const filterChips: PostFilterChipsShape = useMemo(
		() =>
			[
				{
					description: 'followed-teams-post-filter',
					iconEnd: ChevronDownIcon,
					name: $t('feed.followedTeamsFeedFilters.viewByTeam'),
					value: FeedFilterValues.VIEW_BY_TEAM
				},
				{
					description: 'followed-teams-post-filter',
					name:
						type === FeedFiltersType.SUGGESTED_FEED
							? $t('feed.feedFilters.suggested')
							: $t('feed.followedTeamsFeedFilters.all'),
					selected: true,
					value: FeedFilterValues.ALL
				},
				{
					description: 'followed-teams-post-filter',
					disabled: isFavouritePartnerSsu,
					iconStart: SociosEmblemIcon,
					name: $t('feed.followedTeamsFeedFilters.socios'),
					value: FeedFilterValues.SSU
				},
				{
					description: 'followed-teams-post-filter',
					iconStart: MarketplaceIcon,
					name: $t('feed.followedTeamsFeedFilters.withToken'),
					value: FeedFilterValues.WITH_TOKEN
				},
				{
					description: 'followed-teams-post-filter',
					iconStart: PollIcon,
					name: $t('feed.followedTeamsFeedFilters.poll'),
					value: FeedFilterValues.POLL
				},
				{
					description: 'followed-teams-post-filter',
					iconStart: PuzzleIcon,
					name: $t('feed.followedTeamsFeedFilters.game'),
					value: FeedFilterValues.GAMES
				},
				{
					description: 'followed-teams-post-filter',
					disabled: !settings?.isSociosCollectiblesEnabled,
					iconStart: ShirtAuctionIcon,
					name: $t('feed.followedTeamsFeedFilters.auctions'),
					value: FeedFilterValues.AUCTIONS
				}
			].filter(chip => !chip?.disabled && filterChipsByPredicate(chip)),
		[settings, $t, isFavouritePartnerSsu]
	);

	return { filterChips };
};
